import React, { useState, useEffect, useMemo } from "react";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Link } from "react-router-dom";
import { useGetFaqsQuery } from "../../services/auth";
import { PageHero, LoadingState, ErrorState, EmptyState } from "./PublicShared";
import "./PublicPages.scss";

interface FaqItem {
  id: number;
  question: string;
  answer: string;
  is_active?: boolean;
  order?: number;
}

export default function Faq() {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openItems, setOpenItems] = useState<Record<number, boolean>>({ 0: true });

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Query backend FAQs with optional search parameter
  const { data: faqsResponse, isLoading, isError, refetch } = useGetFaqsQuery(
    debouncedSearch ? { search: debouncedSearch } : undefined
  );

  const faqs: FaqItem[] = useMemo(() => {
    const raw = faqsResponse?.data || faqsResponse || [];
    return Array.isArray(raw) ? raw : [];
  }, [faqsResponse]);

  // Automatically open first item when results change and no item is open
  useEffect(() => {
    if (faqs.length > 0) {
      setOpenItems((prev) => (Object.keys(prev).length === 0 ? { 0: true } : prev));
    }
  }, [faqs]);

  const toggleItem = (index: number) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const areAllExpanded = faqs.length > 0 && faqs.every((_, idx) => !!openItems[idx]);

  const toggleAll = () => {
    if (areAllExpanded) {
      setOpenItems({});
    } else {
      const allOpen: Record<number, boolean> = {};
      faqs.forEach((_, idx) => {
        allOpen[idx] = true;
      });
      setOpenItems(allOpen);
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setDebouncedSearch("");
  };

  return (
    <div className="public_page_container">
      <PageHero
        badge="Help Center"
        badgeIcon={<QuizOutlinedIcon />}
        title="Frequently Asked Questions"
        subtitle="Quick answers to common questions about designing, exporting, and managing your wall art."
      />

      <div className="public_card_container">
        {/* Search Bar */}
        <div className="faq_search_bar">
          <div className="search_icon">
            <SearchIcon />
          </div>
          <input
            type="text"
            placeholder="Search questions or keywords (e.g., designs, exports, account)..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search FAQs"
          />
          {searchInput && (
            <button
              className="clear_button"
              onClick={handleClearSearch}
              title="Clear search"
              aria-label="Clear search"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Loading State */}
        {isLoading ? (
          <LoadingState lines={7} />
        ) : isError ? (
          <ErrorState
            title="Failed to load FAQs"
            message="We were unable to load the FAQ list from the server. Please try again."
            onRetry={refetch}
          />
        ) : faqs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <EmptyState
              title="No questions found"
              message={
                debouncedSearch
                  ? `No FAQs matched "${debouncedSearch}". Try a different keyword or check our support desk.`
                  : "No FAQs are currently available."
              }
            />
            {debouncedSearch && (
              <button
                className="btn_retry"
                onClick={handleClearSearch}
                style={{ marginTop: 14 }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Status Bar */}
            <div className="faq_status_bar">
              <div className="faq_count">
                {debouncedSearch ? (
                  <span>
                    Found {faqs.length} question{faqs.length === 1 ? "" : "s"} for "{debouncedSearch}"
                  </span>
                ) : (
                  <span>Showing {faqs.length} questions</span>
                )}
              </div>
              <button className="faq_toggle_all" onClick={toggleAll}>
                {areAllExpanded ? "Collapse All" : "Expand All"}
              </button>
            </div>

            {/* Accordion List */}
            <div className="faq_accordion_list" role="region" aria-label="FAQ list">
              {faqs.map((faq, index) => {
                const isOpen = !!openItems[index];
                return (
                  <div
                    key={faq.id || index}
                    className={`faq_accordion_item ${isOpen ? "open" : ""}`}
                  >
                    <div
                      className="faq_accordion_header"
                      onClick={() => toggleItem(index)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleItem(index);
                        }
                      }}
                    >
                      <h3 className="question_text">{faq.question}</h3>
                      <div className="expand_icon">
                        <KeyboardArrowDownIcon />
                      </div>
                    </div>

                    {isOpen && (
                      <div
                        className="faq_accordion_content"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Support CTA Banner */}
            <div className="faq_support_cta">
              <div className="cta_text">
                <h4>Didn't find what you were looking for?</h4>
                <p>
                  Our support team is happy to assist you with any questions or custom inquiries.
                </p>
              </div>
              <Link to="/public/support" className="cta_btn">
                Contact Support Desk
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
